import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { X, Save } from "lucide-react";
import {
  quotesApi,
  sendQuote,
  resendQuote,
  type QuoteDetailDto,
  type UpdateQuoteRequest,
  type SetQuoteTandCsRequest,
  type SendQuoteRequest,
} from "@/lib/quotes";
import { quoteFilesApi } from "@/lib/quote-files";
import { useAuth } from "@/app/auth-context";
import { SendEmailDialog } from "@/components/email/SendEmailDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DetailPage, type DetailTab } from "@/archetypes/DetailPage";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageContainer } from "@/app/layouts/PageContainer";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { useConfirm } from "@/hooks/use-confirm";
import { type QuoteHeroFormValues } from "./components/QuoteStatusTermsCard";
import { QuoteTab } from "./components/QuoteTab";
import { QuoteHeader } from "./components/QuoteHeader";
import { ProductPicker } from "@/components/products/ProductPicker";
import { MfgBuilderPanel } from "@/components/mfg/builder/MfgBuilderPanel";
import { EntityEventsTab } from "@/components/detail/EntityEventsTab";
import { EntitySpecialNotesTab } from "@/components/detail/EntitySpecialNotesTab";
import { EmailThreadTab } from "@/components/email/EmailThreadTab";
import { QuoteFilesTab } from "@/components/quotes/quote-files-tab";
import { QuoteTabToolbar } from "./components/QuoteTabToolbar";
import { Link } from "react-router";
import { tasksApi } from "@/lib/tasks";
import { TaskStatusBadge } from "@/components/tasks/TaskStatusBadge";
import { TaskTypeBadge } from "@/components/tasks/TaskTypeBadge";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// QuoteTasksTab — live list of tasks linked to this quote (Plan 2 T-9).
// ---------------------------------------------------------------------------

function QuoteTasksTab({ quoteId }: { quoteId: number }) {
  const quoteTasksQuery = useQuery({
    queryKey: ["task-list", "quote", quoteId],
    queryFn: () => tasksApi.list({ quoteId, scope: "all", pageSize: 50 }),
  });

  if (quoteTasksQuery.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  }

  const items = quoteTasksQuery.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        No tasks for this quote yet.
      </p>
    );
  }

  return (
    <ul className="divide-y rounded-md border">
      {items.map((t) => (
        <li key={t.id}>
          <Link
            to={`/tasks/${t.id}`}
            className="flex flex-wrap items-center gap-2 px-3 py-2 hover:bg-muted/50"
          >
            <TaskTypeBadge taskType={t.taskType} />
            <span className="flex-1 truncate text-sm font-medium">{t.title}</span>
            {t.dueDateUtc && (
              <span className="text-xs text-muted-foreground">
                {formatDate(t.dueDateUtc)}
              </span>
            )}
            <TaskStatusBadge status={t.status} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// SkeletonDetail
// ---------------------------------------------------------------------------

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// QuoteDetailPage — Plan VI T11 restructure; Task 9 BOM-flow redesign.
//
// Layout shape (8-tab DetailPage):
//   - DetailPage with `headerVariant="metaStrip"` → Client / Owner / Valid until
//     in a meta strip, status badge as metaStripExtra.
//   - headerActions cluster: Send/Resend, Convert to Job, PDF, Duplicate.
//   - tabs (8): Quote (forceMount) | Client | Event | Special Notes |
//               Files | Message Inbox | Messages Sent | Tasks.
//   - footer = Delete / Cancel / Save.
//
// The Quote tab always renders its content (forceMount keeps form state).
// "Add item → From a product" opens ProductPicker in a Dialog overlay.
// Built lines expand to the inline BuildPricePanel (no separate BOM tab).
// ---------------------------------------------------------------------------

export function QuoteDetailPage() {
  const { id = "0" } = useParams();
  const qid = Number(id);
  const qc = useQueryClient();
  const nav = useNavigate();
  const { has } = usePermissions();
  const { me } = useAuth();
  const confirm = useConfirm();
  const [emailOpen, setEmailOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Task 9: ProductPicker opens in a Dialog; Quote tab always renders its content.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mfgOpen, setMfgOpen] = useState(false);
  // Task 13: when set, the mfg Dialog opens in edit mode against this existing tree BOM.
  const [editMfgBomId, setEditMfgBomId] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["quote", qid],
    queryFn: () => quotesApi.get(qid),
    enabled: !!qid,
  });

  const tandCsQ = useQuery({
    queryKey: ["quote-tandcs", qid],
    queryFn: () => quotesApi.getTandCs(qid),
    enabled: !!qid,
  });

  const update = useMutation({
    mutationFn: async (values: QuoteHeroFormValues) => {
      if (!data) return;
      // Non-null assertion safe: Save button is gated on
      // `data.rowVersionBase64 != null` via the disabled-Tooltip wrapper
      // below. Asserting at the call site makes the contract explicit
      // instead of silently bypassing optimistic concurrency with `?? ""`.
      const patchBody: UpdateQuoteRequest = {
        validUntil: values.validUntil || null,
        approvalTypeCode: values.approvalTypeCode || null,
        purchaseOrder: values.purchaseOrder || null,
        description: values.description || null,
        rowVersionBase64: data.rowVersionBase64!,
      };
      await quotesApi.patch(qid, patchBody);

      // T&Cs use a separate endpoint. Only call when the body actually
      // changed — avoids spurious PUT on plain Save clicks.
      const currentTerms = tandCsQ.data?.tandCsText ?? "";
      if ((values.termsBody ?? "") !== currentTerms) {
        const tandcsBody: SetQuoteTandCsRequest = {
          tandCsText: values.termsBody ?? "",
          quoteTandCsTemplateId: tandCsQ.data?.quoteTandCsTemplateId ?? null,
        };
        await quotesApi.setTandCs(qid, tandcsBody);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      void qc.invalidateQueries({ queryKey: ["quote-tandcs", qid] });
      toast.success("Quote saved.");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", qid] });
        return;
      }
      toast.error(`Save failed: ${(err as Error).message}`);
    },
  });

  const remove = useMutation({
    mutationFn: () => quotesApi.delete(qid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success("Quote deleted.");
      void nav("/quotes");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const duplicate = useMutation({
    mutationFn: () => quotesApi.duplicate(qid),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(`Duplicate quote ${res.quoteRef} created.`);
    },
    onError: (err: Error) => toast.error(`Duplicate failed: ${err.message}`),
  });

  const sendQuoteMut = useMutation({
    mutationFn: ({ kind, ...body }: SendQuoteRequest & { kind: "send" | "resend" }) =>
      kind === "send" ? sendQuote(qid, body) : resendQuote(qid, body),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      void qc.invalidateQueries({ queryKey: ["quote", qid, "messages-sent"] });
      toast.success(`Send succeeded — version ${res.version}`);
    },
    onError: (err: Error) => toast.error(`Send failed: ${err.message}`),
  });

  const approveMut = useMutation({
    mutationFn: () =>
      quotesApi.approve(qid, {
        approverEmployeeId: me!.employeeId!,
        isApproveAny: has("quotes:approve-any"),
        rowVersionBase64: data!.rowVersionBase64!,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      toast.success("Quote approved.");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", qid] });
        return;
      }
      toast.error(`Approve failed: ${(err as Error).message}`);
    },
  });

  const rejectMut = useMutation({
    mutationFn: () =>
      quotesApi.reject(qid, {
        reason: rejectReason,
        rejecterEmployeeId: me!.employeeId!,
        rowVersionBase64: data!.rowVersionBase64!,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      setRejectOpen(false);
      setRejectReason("");
      toast.success("Quote rejected.");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", qid] });
        return;
      }
      toast.error(`Reject failed: ${(err as Error).message}`);
    },
  });

  const recallMut = useMutation({
    mutationFn: () =>
      quotesApi.recall(qid, { rowVersionBase64: data!.rowVersionBase64! }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      toast.success("Quote recalled.");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", qid] });
        return;
      }
      toast.error(`Recall failed: ${(err as Error).message}`);
    },
  });

  // Backlog #9 — manual totals override (Costs card Edit / Revert).
  const setManualTotals = useMutation({
    mutationFn: (totalExGst: number | null) =>
      quotesApi.setManualTotals(qid, {
        totalExGst,
        rowVersionBase64: data!.rowVersionBase64!,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", qid] });
      toast.success("Totals updated.");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This quote changed since you opened it — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote", qid] });
        return;
      }
      toast.error(`Update failed: ${(err as Error).message}`);
    },
  });

  const form = useForm<QuoteHeroFormValues>({
    values: data
      ? {
          validUntil: data.validUntil ?? "",
          approvalTypeCode: data.approvalTypeCode ?? "",
          termsBody: tandCsQ.data?.tandCsText ?? "",
          purchaseOrder: data.purchaseOrder ?? "",
          description: data.description ?? "",
        }
      : undefined,
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Quote not found.</p>
      </PageContainer>
    );
  }

  const d: QuoteDetailDto = data;

  const handleSave = form.handleSubmit(async (values) => {
    await update.mutateAsync(values);
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete this quote?",
      body: "This action cannot be undone.",
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (!ok) return;
    await remove.mutateAsync();
  };

  // --- Footer ----------------------------------------------------------------
  // Two gates:
  //   editable  — quote must be in Draft|Sent to allow edits (backend enforces 409).
  //   canSave   — rowVersion must be present (optimistic-concurrency guard).
  // Cancel/Delete don't touch either gate.
  const editable = ["Draft", "Sent"].includes(d.status);
  const canSave = d.rowVersionBase64 != null;

  let saveTooltip: string | null = null;
  if (!editable) {
    saveTooltip = "This quote can't be edited in its current status.";
  } else if (!canSave) {
    saveTooltip = "Concurrency token missing — refresh the page to enable saving.";
  }

  const footer = (
    <div className="flex justify-between items-center w-full gap-4 pt-4 mt-6">
      <div>
        <Button
          type="button"
          variant="outline"
          className="rounded-full px-6 bg-white border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 font-semibold shadow-sm"
          onClick={() => void handleDelete()}
          disabled={remove.isPending}
        >
          <X className="w-4 h-4 mr-2" />
          Delete Quote
        </Button>
      </div>
      <div className="flex items-center gap-4">
        <Button variant="outline" className="rounded-full px-6 bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-semibold shadow-sm" onClick={() => void nav("/quotes")}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        {saveTooltip == null ? (
          <Button
            type="button"
            className="rounded-full px-8 bg-[#0f3453] hover:bg-[#1a2f4c] text-white border-none font-semibold"
            onClick={() => void handleSave()}
            disabled={update.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {update.isPending ? "Saving…" : "Save Quote"}
          </Button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span role="button" aria-disabled="true" tabIndex={0}>
                  <Button type="button" disabled className="rounded-full px-8 font-semibold">
                    <Save className="w-4 h-4 mr-2" />
                    Save Quote
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {saveTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );

  const tabs: DetailTab[] = [
    {
      id: "quote",
      label: "Quote",
      forceMount: true,
      content: (
        <QuoteTab
          quote={d}
          onAddFromProduct={() => setPickerOpen(true)}
          onAddManufacturing={has("mfg-bom:write") ? () => { setEditMfgBomId(null); setMfgOpen(true); } : undefined}
          onEditManufacturing={has("mfg-bom:write") ? (id) => { setEditMfgBomId(id); setMfgOpen(true); } : undefined}
          onSetManualTotals={(v) => setManualTotals.mutate(v)}
        />
      ),
    },
    {
      id: "special-notes",
      label: "Special Notes",
      content: (
        <EntitySpecialNotesTab
          context={{ kind: "quote", id: d.quoteId, rowVersionBase64: d.rowVersionBase64 }}
        />
      ),
    },
    {
      id: "files",
      label: "Files",
      content: (
        <QuoteFilesTab
          quoteId={d.quoteId}
          canUpload={has("quotes:write")}
          canDelete={has("quotes:delete")}
        />
      ),
    },
    {
      id: "emails",
      label: "Emails",
      content: <EmailThreadTab entityType="Quote" entityId={d.quoteId} defaultTo={d.contactEmail ?? undefined} />,
    },
    {
      id: "tasks",
      label: "Tasks",
      content: <QuoteTasksTab quoteId={d.quoteId} />,
    },
    {
      id: "event",
      label: "Event",
      content: <EntityEventsTab context={{ kind: "quote", id: d.quoteId }} />,
    },
  ];

  return (
    <>
      <FormProvider {...form}>
        <DetailPage
          title={d.quoteRef}
          headerOverride={<QuoteHeader quote={d} />}
          footer={footer}
          backTo="/quotes"
          tabs={tabs}
          defaultTab="quote"
          tabActions={
            <QuoteTabToolbar
              quote={d}
              quoteId={d.quoteId}
              canConvert={has("jobs:write")}
              canApprove={has("quotes:approve") && me?.employeeId != null}
              onCopy={() => duplicate.mutate()}
              onEmail={() => setEmailOpen(true)}
              onApprove={() => {
                void confirm({ title: "Approve quote?", confirmLabel: "Approve" }).then((ok) => {
                  if (ok) approveMut.mutate();
                });
              }}
              onReject={() => setRejectOpen(true)}
              onRecall={() => {
                void confirm({ title: "Recall approval?", confirmLabel: "Recall", intent: "destructive" }).then((ok) => {
                  if (ok) recallMut.mutate();
                });
              }}
              copyPending={duplicate.isPending}
              sendPending={sendQuoteMut.isPending}
              approvalPending={approveMut.isPending || rejectMut.isPending || recallMut.isPending}
            />
          }
        />
      </FormProvider>

      {/* Task 9: ProductPicker in a Dialog — Quote tab always shows its content. */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add an item from a product</DialogTitle>
            <DialogDescription className="sr-only">
              Pick a product to add a new built line to this quote.
            </DialogDescription>
          </DialogHeader>
          <ProductPicker
            context={{ kind: "quote", id: d.quoteId }}
            onCancel={() => setPickerOpen(false)}
            onAdded={() => setPickerOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Task 12/13: MfgBuilderPanel in a Dialog — add (editMfgBomId null) or edit an existing line. */}
      <Dialog open={mfgOpen} onOpenChange={(o) => { setMfgOpen(o); if (!o) setEditMfgBomId(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMfgBomId != null ? "Edit manufacturing item" : "Add a manufacturing item"}</DialogTitle>
            <DialogDescription className="sr-only">Configure a tree BOM and price it.</DialogDescription>
          </DialogHeader>
          <MfgBuilderPanel
            key={editMfgBomId ?? "new"}
            context={{ kind: "quote", id: d.quoteId }}
            mfgBomId={editMfgBomId ?? undefined}
            onCancel={() => { setMfgOpen(false); setEditMfgBomId(null); }}
            onAdded={() => { setMfgOpen(false); setEditMfgBomId(null); }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={(o) => { if (!o) { setRejectOpen(false); setRejectReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="reject-reason" className="text-sm font-medium">
              Reason <span className="text-muted-foreground">(required)</span>
            </label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectOpen(false); setRejectReason(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectReason.trim() === "" || rejectMut.isPending}
              onClick={() => rejectMut.mutate()}
            >
              {rejectMut.isPending ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        defaultTo={d.contactEmail ?? ""}
        defaultSubject={`Quote ${d.quoteRef}`}
        defaultBody=""
        onUploadFromComputer={async (file) => {
          const up = await quoteFilesApi.upload(qid, file);
          return {
            id: String(up.file.quoteFileId),
            name: up.file.originalFileName,
            source: "computer" as const,
          };
        }}
        onPickFromLibrary={async () => {
          const files = await quoteFilesApi.list(qid);
          return files.map((f) => ({
            id: String(f.quoteFileId),
            name: f.originalFileName,
            source: "library" as const,
          }));
        }}
        onUploadInlineImage={async (file) => {
          const up = await quoteFilesApi.upload(qid, file);
          return {
            cid: `qf${up.file.quoteFileId}`,
            url: quoteFilesApi.downloadUrl(qid, up.file.quoteFileId),
            attachmentId: String(up.file.quoteFileId),
          };
        }}
        onSend={async (payload) => {
          const kind = d.status === "Draft" ? "send" : "resend";
          const inlineImages = payload.inlineImages.map((im) => ({
            cid: im.cid,
            quoteFileId: Number(im.attachmentId),
          }));
          const inlineIds = new Set(inlineImages.map((im) => im.quoteFileId));
          const attachmentQuoteFileIds = payload.attachmentIds
            .map(Number)
            .filter((id) => !inlineIds.has(id));
          await sendQuoteMut.mutateAsync({
            kind,
            customerEmail: payload.to,
            cc: payload.cc,
            bcc: payload.bcc,
            subject: payload.subject,
            htmlBody: payload.htmlBody,
            attachmentQuoteFileIds,
            inlineImages,
            includePdf: true,
          });
        }}
      />
    </>
  );
}
